import React, { useState } from "react";
import { ArrowDown, ChevronDown, ChevronUp } from "lucide-react";

export interface PlateOptimisationData {
  tip_type: string;
  headline: string;
  subtext: string;
  stats: {
    peak_reduction_pct: number;
    food_change: number;
    effort_min: number;
  };
  steps: Array<{
    order: number;
    dot_type: "green" | "amber" | "gray";
    label: string;
    sub: string;
  }>;
  ordering_steps?: Array<{
    order: number;
    dot_type: "green" | "amber" | "gray";
    label: string;
    sub: string;
  }>;
  triggered_by: string;
}

interface PlateOptimisationCardProps {
  plateData: PlateOptimisationData;
}

const INITIAL_VISIBLE = 4;

// Single consistent accent for ALL stepper components (dots, connector lines
// and arrows) so the whole stepper shares one background color.
const STEP_LINE_CLASS = "bg-indigo-500";
const DOT_CLASS = "bg-indigo-500 text-white";
const ARROW_CLASS = "text-indigo-500";

const PlateOptimisationCard: React.FC<PlateOptimisationCardProps> = ({ plateData }) => {
  const [showAll, setShowAll] = useState(false);

  // Conditionally select steps based on tip_type
  let allSteps: Array<{
    order: number;
    dot_type: "green" | "amber" | "gray";
    label: string;
    sub: string;
  }>;

  if (plateData.tip_type === "eating_order") {
    allSteps = plateData.steps;
  } else {
    // For other tip_types, use ordering_steps only - if not available, hide the card
    if (!plateData.ordering_steps || plateData.ordering_steps.length === 0) {
      return null;
    }
    allSteps = plateData.ordering_steps;
  }
  
  const steps = [...allSteps].sort((a, b) => a.order - b.order);
  const visibleSteps = showAll ? steps : steps.slice(0, INITIAL_VISIBLE);
  const hasMore = steps.length > INITIAL_VISIBLE;

  const statItems = [
    { value: `~${plateData.stats.peak_reduction_pct}%`, label: "lower glucose peak" },
    { value: String(plateData.stats.food_change), label: "food changes" },
    { value: `${plateData.stats.effort_min} min`, label: "extra effort" },
  ];

  return (
    <div className="rounded-2xl overflow-hidden border border-border bg-card shadow-md">
      {/* Header */}
      <div className="px-4 pt-5 pb-4 bg-indigo-50 border-b border-indigo-100">
        <div className="inline-flex items-center px-2.5 py-0.5 rounded-full mb-3 bg-indigo-100 border border-indigo-200">
          <span className="text-caption2 font-bold tracking-widest uppercase text-indigo-600">
            Plate Optimisation
          </span>
        </div>
        <h2 className="text-headline font-bold leading-tight mb-1.5 text-foreground">
          {plateData.headline}
        </h2>
        <p className="text-footnote text-muted-foreground leading-snug">
          {plateData.subtext}
        </p>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-3 divide-x divide-border border-b border-border bg-card">
        {statItems.map((s) => (
          <div key={s.label} className="flex flex-col items-center gap-0.5 px-2 py-3">
            <span className="text-callout font-extrabold leading-none text-emerald-600">
              {s.value}
            </span>
            <span className="text-caption2 font-medium text-center text-muted-foreground">
              {s.label}
            </span>
          </div>
        ))}
      </div>

      {/* Steps list */}
      <div className="px-4 pt-4 pb-4 bg-card">
        <p className="text-caption2 font-bold tracking-widest uppercase mb-3 text-muted-foreground">
          Eat in this order
        </p>
    
        <div className="relative">
          <div className="flex flex-col gap-2">
            {visibleSteps.map((step, index) => {
              const isFirst = index === 0;
              const isLast = index === visibleSteps.length - 1;
              return (
                <div key={step.order} className="flex gap-3">
                  <div className="relative flex-shrink-0 w-7 self-stretch">
                    {!isFirst && (
                      <div className={`absolute left-1/2 top-0 bottom-1/2 -translate-x-1/2 w-0.5 ${STEP_LINE_CLASS}`} />
                    )}
                    {!isLast && (
                      <div className={`absolute left-1/2 top-1/2 bottom-0 -translate-x-1/2 w-0.5 ${STEP_LINE_CLASS}`} />
                    )}
                    {!isLast && (
                      <div className="absolute left-1/2 bottom-0 -translate-x-1/2 translate-y-1/2 z-20 rounded-full bg-card p-0.5">
                        <ArrowDown size={12} className={ARROW_CLASS} />
                      </div>
                    )}
                    <div
                      className={`absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10 w-7 h-7 rounded-full flex items-center justify-center text-caption1 font-bold shadow-sm ${DOT_CLASS}`}
                    >
                      {step.order}
                    </div>
                  </div>
                  <div className="flex-1 flex items-center gap-3 rounded-xl px-3 py-3 text-left border bg-background border-border">
                    <div className="flex-1 min-w-0">
                      <p className="text-subhead font-semibold text-foreground">
                        {step.label}
                      </p>
                      <p className="text-caption1 mt-0.5 leading-snug text-muted-foreground">
                        {step.sub}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {hasMore && (
          <button
            onClick={() => setShowAll((v) => !v)}
            className="mt-3 w-full flex items-center justify-center gap-1.5 rounded-xl py-2.5 text-subhead font-semibold border border-border bg-muted/30 text-muted-foreground transition-colors hover:bg-muted/60"
          >
            {showAll ? (
              <>Show less <ChevronUp size={15} /></>
            ) : (
              <>Show {steps.length - INITIAL_VISIBLE} more <ChevronDown size={15} /></>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

export default PlateOptimisationCard;
